// ============================================
// FAQ 编辑器弹窗（全屏 + 左侧目录大纲侧边栏）
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Loader2, Save, Eye, EyeOff, Upload, Image as ImageIcon,
  Clipboard, ArrowLeft, List, ChevronRight, Paperclip, FileText, Download, Trash2,
  Link2, Code, Bold, Italic, Strikethrough, Quote,
  Minus, Terminal, Table, Lock, Clock,
} from 'lucide-react';
import FaqRichEditor from './FaqRichEditor';
import FaqHtmlSandboxPreview from './FaqHtmlSandboxPreview';
import FaqAttachmentPreview from './FaqAttachmentPreview';
import { uploadFaqImage, uploadFaqFile } from './faq-service';
import { useFaqStore } from './faq-store';
import { richTextHasContent } from './faq-rich-content';
import { tagIdsFromNames, tagPayloadFromIds } from './faq-tag-utils';
import { useFaqSpace } from './FaqSpaceContext';
import type { FaqItem, FaqAttachment, FaqAnswerFormat, FaqVisibilityScope } from './types';

// ── 全局工具栏辅助组件（必须定义在组件外部，避免每次渲染都销毁/重建 DOM 导致闪烁） ──

function GBtn({ active, onClick, title, children }: {
  active: boolean; onClick: () => void; title: string; children: React.ReactNode;
}) {
  return (
    <button
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick} title={title}
      className={`w-8 h-8 flex items-center justify-center rounded-md transition-all ${
        active ? 'bg-sky-100 text-sky-600 font-semibold shadow-sm' : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-700'
      }`}
    >{children}</button>
  );
}

function GSep() {
  return <div className="w-px h-5 bg-slate-200/80 mx-1" />;
}

interface HeadingItem {
  id: string;
  text: string;
  level: number;
}

interface FaqEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingItem?: FaqItem | null;
  initialDraft?: {
    question?: string;
    answer?: string;
    notes?: string;
    primaryTags?: string[];
    secondaryTags?: string[];
    primaryTagIds?: string[];
    secondaryTagIds?: string[];
  } | null;
  onSaved?: (item: FaqItem) => void;
  /** 编辑锁即将过期（父组件传入） */
  lockExpiring?: boolean;
  /** 继续编辑回调（父组件传入） */
  onRefreshActivity?: () => void;
}

export default function FaqEditorModal({ isOpen, onClose, editingItem, initialDraft, onSaved, onRefreshActivity }: FaqEditorModalProps) {
  const { space } = useFaqSpace();
  const { createItem, updateItem, deleteItem, getPrimaryTags, getSecondaryTags } = useFaqStore();

  const [question, setQuestion] = useState('');
  const [answerJson, setAnswerJson] = useState('');
  const [answerHtml, setAnswerHtml] = useState('');
  const [answerMode, setAnswerMode] = useState<FaqAnswerFormat>('tiptap');
  const [htmlPane, setHtmlPane] = useState<'code' | 'preview'>('code');
  const [notesJson, setNotesJson] = useState('');
  const [primaryTagIds, setPrimaryTagIds] = useState<string[]>([]);
  const [secondaryTagIds, setSecondaryTagIds] = useState<string[]>([]);
  const [coverImage, setCoverImage] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const [visibilityScope, setVisibilityScope] = useState<FaqVisibilityScope>('all');
  const [saving, setSaving] = useState(false);
  const [savedHint, setSavedHint] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const [coverUploading, setCoverUploading] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [coverExpanded, setCoverExpanded] = useState(false);
  const [attachments, setAttachments] = useState<FaqAttachment[]>([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<FaqAttachment | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [createdItem, setCreatedItem] = useState<FaqItem | null>(null);

  // 顶层全局编辑操作栏状态
  const [activeEditor, setActiveEditor] = useState<any>(null);
  const [, forceToolbarUpdate] = useState(0);
  const [globalToolbarSubMenu, setGlobalToolbarSubMenu] = useState<'none' | 'color' | 'listStyle'>('none');

  // 目录大纲
  const [editorHeadings, setEditorHeadings] = useState<HeadingItem[]>([]);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const editorRef = useRef<any>(null);
  const answerEditorRef = useRef<any>(null);
  const notesEditorRef = useRef<any>(null);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const pasteAreaRef = useRef<HTMLDivElement>(null);
  const savingRef = useRef(false);

  // ── 编辑倒计时（自包含，不依赖外部 Hook） ──
  const IDLE_LIMIT = 3 * 60; // 3 分钟无操作则自动保存退出（秒）
  const editStartRef = useRef<number>(Date.now());
  const lastActivityRef = useRef<number>(Date.now());
  const [elapsed, setElapsed] = useState(0);        // 已编辑时长（秒）
  const [idleRemaining, setIdleRemaining] = useState(IDLE_LIMIT); // 剩余空闲时间（秒）
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoSaveRef = useRef<() => void>(() => {}); // 始终持有最新的保存函数
  const pendingUploadTasksRef = useRef<Set<Promise<void>>>(new Set());

  const allPrimaryTags = getPrimaryTags();
  const allSecondaryTags = getSecondaryTags();
  const allTags = [...allPrimaryTags, ...allSecondaryTags];
  const uploadImage = (file: File) => uploadFaqImage(file, space.slug);
  const uploadFile = (file: File) => uploadFaqFile(file, space.slug);

  // 统一管理当前正聚焦的编辑器实例
  const handleEditorFocus = useCallback((editor: any) => {
    setActiveEditor(editor);
  }, []);

  const registerUploadTask = useCallback((task: Promise<void>) => {
    pendingUploadTasksRef.current.add(task);
    setPendingUploadCount(pendingUploadTasksRef.current.size);

    const cleanup = () => {
      pendingUploadTasksRef.current.delete(task);
      setPendingUploadCount(pendingUploadTasksRef.current.size);
    };
    task.then(cleanup, cleanup);
  }, []);

  const waitForPendingUploads = useCallback(async () => {
    while (pendingUploadTasksRef.current.size > 0) {
      await Promise.allSettled(Array.from(pendingUploadTasksRef.current));
    }
  }, []);

  // GBtn 与 GSep 已提升至模块顶层，避免闪烁

  // GBtn 与 GSep 已提升至模块顶层，避免闪烁
  useEffect(() => {
    setCreatedItem(null);
    if (editingItem) {
      const nextAnswerMode: FaqAnswerFormat = editingItem.answer_format === 'html' ? 'html' : 'tiptap';
      setQuestion(editingItem.question);
      setAnswerMode(nextAnswerMode);
      setHtmlPane('code');
      setAnswerJson(nextAnswerMode === 'tiptap' ? editingItem.answer || '' : '');
      setAnswerHtml(nextAnswerMode === 'html' ? editingItem.answer || '' : '');
      setNotesJson(editingItem.notes || '');
      const resolvedPrimaryIds = editingItem.primary_tag_ids?.length
        ? editingItem.primary_tag_ids
        : tagIdsFromNames(editingItem.primary_tags, allTags, 1);
      const resolvedSecondaryIds = editingItem.secondary_tag_ids?.length
        ? editingItem.secondary_tag_ids
        : tagIdsFromNames(editingItem.secondary_tags, allTags, 2);
      setPrimaryTagIds(resolvedPrimaryIds);
      setSecondaryTagIds(resolvedSecondaryIds);
      setCoverImage(editingItem.cover_image || '');
      setAttachments(editingItem.attachments || []);
      setIsPublished(editingItem.is_published);
      setVisibilityScope(editingItem.visibility_scope === 'internal' ? 'internal' : 'all');
    } else {
      setQuestion(initialDraft?.question || '');
      setAnswerMode('tiptap');
      setHtmlPane('code');
      setAnswerJson(initialDraft?.answer || '');
      setAnswerHtml('');
      setNotesJson(initialDraft?.notes || '');
      const initialPrimaryIds = initialDraft?.primaryTagIds
        ?? tagIdsFromNames(initialDraft?.primaryTags, allTags, 1);
      const initialSecondaryIds = initialDraft?.secondaryTagIds
        ?? tagIdsFromNames(initialDraft?.secondaryTags, allTags, 2);
      setPrimaryTagIds(initialPrimaryIds.length > 0 ? initialPrimaryIds : (allPrimaryTags[0] ? [allPrimaryTags[0].id] : []));
      setSecondaryTagIds(initialSecondaryIds);
      setCoverImage('');
      setAttachments([]);
      setIsPublished(true);
      setVisibilityScope('all');
    }
    setPasteMode(false);
    setCoverExpanded(!!(editingItem?.cover_image));
    setEditorHeadings([]);
    setActiveHeadingId('');
    setActiveEditor(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem, initialDraft, isOpen]);

  // 封面图上传
  async function handleCoverUpload(file: File) {
    if (!file.type.startsWith('image/')) return;
    const uploadTask = (async () => {
      setCoverUploading(true);
      try {
        const url = await uploadImage(file);
        setCoverImage(url);
        setPasteMode(false);
      } catch {
        // 上传失败
      } finally {
        setCoverUploading(false);
      }
    })();
    registerUploadTask(uploadTask);
    await uploadTask;
  }

  // 附件文件上传
  async function handleFileUpload(files: FileList | File[]) {
    const uploadTask = (async () => {
      setFileUploading(true);
      try {
        for (const file of Array.from(files)) {
          const result = await uploadFile(file);
          setAttachments((prev) => [...prev, result]);
        }
      } catch (err) {
        console.error('File upload failed:', err);
      } finally {
        setFileUploading(false);
      }
    })();
    registerUploadTask(uploadTask);
    await uploadTask;
  }

  // 粘贴图片监听
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await handleCoverUpload(file);
        return;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // editor 就绪回调 — 注册标题提取
  const handleEditorReady = useCallback((editor: any) => {
    answerEditorRef.current = editor;
    editorRef.current = editor;
    setActiveEditor(editor);
    const extractHeadings = () => {
      const container = contentAreaRef.current?.querySelector('.ProseMirror');
      if (!container) return;

      const elements = container.querySelectorAll('h1, h2, h3');
      const list: HeadingItem[] = [];
      elements.forEach((el, index) => {
        const id = `faq-heading-${index}`;
        el.setAttribute('id', id);
        const text = el.textContent || '';
        if (text.trim()) {
          list.push({ id, text, level: parseInt(el.tagName[1]) });
        }
      });
      setEditorHeadings(list);
    };

    const timer = setTimeout(extractHeadings, 100);
    editor.on('update', extractHeadings);

    return () => {
      clearTimeout(timer);
      editor.off('update', extractHeadings);
    };
  }, []);

  // 监听当前活跃编辑器的选区/操作更新事件，从而重新渲染全局工具操作栏高亮
  useEffect(() => {
    if (!activeEditor) return;
    const updateHandler = () => {
      forceToolbarUpdate((prev) => prev + 1);
    };
    // 只监听 selectionUpdate 即可，transaction 太频繁且不必要
    activeEditor.on('selectionUpdate', updateHandler);
    return () => {
      activeEditor.off('selectionUpdate', updateHandler);
    };
  }, [activeEditor]);

  // 点击外部关闭全局工具栏子菜单
  useEffect(() => {
    if (globalToolbarSubMenu !== 'none') {
      const handler = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.global-toolbar-btn') && !target.closest('.global-toolbar-panel')) {
          setGlobalToolbarSubMenu('none');
        }
      };
      setTimeout(() => document.addEventListener('click', handler), 0);
      return () => document.removeEventListener('click', handler);
    }
  }, [globalToolbarSubMenu]);

  // 仅在编辑器实例切换时折叠子菜单（不再因 selectionTrigger 频繁触发）
  useEffect(() => {
    setGlobalToolbarSubMenu('none');
  }, [activeEditor]);

  // 滚动监听 — 自动高亮当前可见标题
  useEffect(() => {
    if (editorHeadings.length === 0 || !contentAreaRef.current) return;

    const scrollEl = contentAreaRef.current;
    const handleScroll = () => {
      const scrollPos = scrollEl.scrollTop + 180;
      let currentId = '';
      for (const h of editorHeadings) {
        const el = document.getElementById(h.id);
        if (el && el.offsetTop <= scrollPos) {
          currentId = h.id;
        }
      }
      setActiveHeadingId(currentId);
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [editorHeadings]);

  // 点击标题跳转
  function scrollToHeading(heading: HeadingItem) {
    if (!editorRef.current) return;
    const domIndex = parseInt(heading.id.replace('faq-heading-', ''));
    let count = 0;
    let targetPos = -1;

    editorRef.current.state.doc.descendants((node: any, pos: number) => {
      if (node.type.name === 'heading') {
        if (count === domIndex) targetPos = pos;
        count++;
      }
    });

    if (targetPos >= 0) {
      editorRef.current.chain().setTextSelection(targetPos + 1).focus().scrollIntoView().run();
    }
  }

  function handleAnswerModeChange(nextMode: FaqAnswerFormat) {
    if (nextMode === answerMode) return;

    const hasCurrentAnswer = answerMode === 'html'
      ? answerHtml.trim().length > 0
      : richTextHasContent(answerJson);

    if (hasCurrentAnswer) {
      const message = nextMode === 'html'
        ? '切换到 HTML 模式后，本次保存会把回答保存为原始 HTML；当前富文本不会自动转换为 HTML。确定切换吗？'
        : '切换到富文本模式后，本次保存会把回答保存为富文本内容；当前 HTML 不会自动转换为富文本。确定切换吗？';
      if (!window.confirm(message)) return;
    }

    setAnswerMode(nextMode);
    setHtmlPane('code');
    setActiveEditor(null);
    if (nextMode === 'html') {
      setEditorHeadings([]);
      setActiveHeadingId('');
    }
  }

  // 保存（shouldClose=false 则不退出，shouldClose=true 则退出）
  async function handleSave(shouldClose = false, publishOverride?: boolean) {
    if (!question.trim() || primaryTagIds.length === 0) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSavedHint(false);
    setSaveMessage(null);
    try {
      if (pendingUploadTasksRef.current.size > 0) {
        setSaveMessage('图片/附件上传中，保存将在上传完成后继续...');
        await waitForPendingUploads();
      }

      setSaveMessage('保存中...');
      const published = publishOverride !== undefined ? publishOverride : isPublished;
      const currentAnswer = answerMode === 'html'
        ? answerHtml
        : answerEditorRef.current && !(answerEditorRef.current as any).isDestroyed
          ? JSON.stringify(answerEditorRef.current.getJSON())
          : answerJson;
      const currentNotesJson = notesEditorRef.current && !(notesEditorRef.current as any).isDestroyed
        ? JSON.stringify(notesEditorRef.current.getJSON())
        : notesJson;

      if (answerMode === 'html') {
        setAnswerHtml(currentAnswer);
      } else {
        setAnswerJson(currentAnswer);
      }
      setNotesJson(currentNotesJson);

      const tagPayload = tagPayloadFromIds(primaryTagIds, secondaryTagIds, allTags);
      const data = {
        question: question.trim(),
        answer: currentAnswer.trim() ? currentAnswer : null,
        answer_format: answerMode,
        notes: currentNotesJson || null,
        cover_image: coverImage || null,
        ...tagPayload,
        attachments: attachments,
        is_published: published,
        visibility_scope: visibilityScope,
      };

      let savedItem: FaqItem | null = null;
      const existingItemId = editingItem?.id ?? createdItem?.id;
      if (existingItemId) {
        savedItem = await updateItem(existingItemId, data);
      } else {
        savedItem = await createItem(data);
      }
      if (savedItem && !editingItem) setCreatedItem(savedItem);
      if (savedItem) onSaved?.(savedItem);

      setSaveMessage(null);
      if (shouldClose) {
        onClose();
      } else {
        // 保存成功提示
        setSavedHint(true);
        setTimeout(() => setSavedHint(false), 2000);
      }
    } catch (e: any) {
      console.error('Save failed:', e);
      setSaveMessage('保存失败，请稍后重试');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  // 始终保持最新的 handleSave 引用（解决 setInterval 闭包陷阱）
  useEffect(() => {
    autoSaveRef.current = () => handleSave(true);
  });

  // ── 编辑器内活动检测 + 秒级定时器 ──
  useEffect(() => {
    if (!isOpen) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }

    // 编辑器打开时重置
    editStartRef.current = Date.now();
    lastActivityRef.current = Date.now();
    setElapsed(0);
    setIdleRemaining(IDLE_LIMIT);

    // 监听编辑器内的活动
    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'input'] as const;
    events.forEach((e) => document.addEventListener(e, markActive, { passive: true }));

    // 每秒更新
    let autoSaved = false;
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const newElapsed = Math.floor((now - editStartRef.current) / 1000);
      const idleSec = Math.floor((now - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, IDLE_LIMIT - idleSec);

      setElapsed(newElapsed);
      setIdleRemaining(remaining);

      // 空闲时间耗尽 → 自动保存并退出（只触发一次）
      if (remaining <= 0 && !autoSaved) {
        autoSaved = true;
        autoSaveRef.current();
      }
    }, 1000);

    return () => {
      events.forEach((e) => document.removeEventListener(e, markActive));
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [isOpen]);

  // 格式化秒 → mm:ss
  function fmtTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* ── 顶栏 ── */}
      <div className="flex items-center justify-between px-3 sm:px-6 h-[56px] shrink-0 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg text-sm font-medium transition-all"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">返回</span>
          </button>
          <div className="w-px h-5 bg-slate-200" />
          <h2 className="text-sm font-semibold text-slate-600">
            {editingItem || createdItem ? '编辑 FAQ' : '新建 FAQ'}
          </h2>

          {/* ── 编辑倒计时指示器 ── */}
          <div className="hidden sm:flex items-center gap-2 ml-3 px-3 py-1 rounded-full bg-slate-50 border border-slate-100 text-xs select-none">
            <span className="text-slate-400">已编辑</span>
            <span className="font-mono font-semibold text-slate-600">{fmtTime(elapsed)}</span>
            <span className="text-slate-300">|</span>
            <Lock size={11} className={idleRemaining <= 60 ? 'text-red-400 animate-pulse' : 'text-slate-400'} />
            <span className={idleRemaining <= 60 ? 'text-red-500 font-semibold' : 'text-slate-400'}>
              空闲 <span className="font-mono font-semibold">{fmtTime(idleRemaining)}</span> 后自动保存
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* 保存成功提示 */}
          {savedHint && (
            <span className="text-xs text-emerald-500 font-medium animate-pulse">✓ 已保存</span>
          )}
          {saveMessage && (
            <span className={`hidden sm:inline text-xs font-medium ${saveMessage.includes('失败') ? 'text-rose-500' : 'text-amber-500'}`}>
              {saveMessage}
            </span>
          )}
          {!saving && pendingUploadCount > 0 && !saveMessage && (
            <span className="hidden sm:inline text-xs text-amber-500 font-medium">图片/附件上传中...</span>
          )}

          {/* 发布状态切换 */}
          <button
            onClick={() => setIsPublished(!isPublished)}
            className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              isPublished ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {isPublished ? <Eye size={13} /> : <EyeOff size={13} />}
            <span className="hidden sm:inline">{isPublished ? '已发布' : '草稿'}</span>
          </button>
          <span className={`hidden sm:inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
            visibilityScope === 'internal' ? 'bg-amber-50 text-amber-600' : 'bg-sky-50 text-sky-600'
          }`}>
            {visibilityScope === 'internal' ? '仅内部可见' : '外部可见'}
          </span>

          {/* 保存（不退出） */}
          <button
            onClick={() => handleSave(false)}
            disabled={saving || !question.trim() || primaryTagIds.length === 0}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-slate-100 text-slate-600 text-sm font-medium rounded-full hover:bg-slate-200 disabled:opacity-50 transition-all"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            <span className="hidden sm:inline">{saving ? (pendingUploadCount > 0 ? '等待上传...' : '保存中...') : '保存'}</span>
          </button>

          {/* 发布并退出 */}
          <button
            onClick={() => {
              setIsPublished(true);
              handleSave(true, true);
            }}
            disabled={saving || !question.trim() || primaryTagIds.length === 0}
            className="flex items-center gap-1.5 px-3 sm:px-5 py-2 bg-sky-500 text-white text-sm font-semibold rounded-full hover:bg-sky-600 disabled:opacity-50 transition-all shadow-sm"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            <span className="hidden sm:inline">{saving ? (pendingUploadCount > 0 ? '等待上传...' : '发布中...') : '发布'}</span>
          </button>

          {/* 删除按钮（仅编辑已有 FAQ 时显示） */}
          {editingItem && (
            <>
              <div className="w-px h-5 bg-slate-200 mx-1 hidden sm:block" />
              <button
                onClick={() => setDeleteConfirm(true)}
                className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 text-xs font-medium rounded-lg transition-all"
              >
                <Trash2 size={13} />
                <span className="hidden sm:inline">删除</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── WPS 样式全局编辑工具操作栏 ── */}
      {!pasteMode && activeEditor && (answerMode === 'tiptap' || activeEditor !== answerEditorRef.current) && (
        <div className="h-[52px] shrink-0 border-b border-slate-200 bg-slate-50 flex items-center justify-center px-4 gap-0.5 select-none overflow-x-auto">
          {/* 段落/标题类型 — 平铺按钮 */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => activeEditor.chain().focus().setParagraph().run()}
            className={`px-2.5 py-1 rounded-md text-[13px] font-semibold transition-all ${
              activeEditor.isActive('paragraph') && !activeEditor.isActive('heading')
                ? 'bg-sky-100 text-sky-600 shadow-sm' : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-700'
            }`}
            title="正文"
          >正文</button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => activeEditor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`w-8 h-8 flex items-center justify-center rounded-md text-[13px] font-bold transition-all ${
              activeEditor.isActive('heading', { level: 1 })
                ? 'bg-sky-100 text-sky-600 shadow-sm' : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-700'
            }`}
            title="一级标题"
          >H1</button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => activeEditor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`w-8 h-8 flex items-center justify-center rounded-md text-[13px] font-bold transition-all ${
              activeEditor.isActive('heading', { level: 2 })
                ? 'bg-sky-100 text-sky-600 shadow-sm' : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-700'
            }`}
            title="二级标题"
          >H2</button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => activeEditor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`w-8 h-8 flex items-center justify-center rounded-md text-[13px] font-bold transition-all ${
              activeEditor.isActive('heading', { level: 3 })
                ? 'bg-sky-100 text-sky-600 shadow-sm' : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-700'
            }`}
            title="三级标题"
          >H3</button>
          <GSep />

          {/* 格式按钮 */}
          <GBtn active={activeEditor.isActive('bold')} onClick={() => activeEditor.chain().focus().toggleBold().run()} title="加粗 ⌘B">
            <Bold size={16} />
          </GBtn>
          <GBtn active={activeEditor.isActive('italic')} onClick={() => activeEditor.chain().focus().toggleItalic().run()} title="斜体 ⌘I">
            <Italic size={16} />
          </GBtn>
          <GBtn active={activeEditor.isActive('underline')} onClick={() => activeEditor.chain().focus().toggleMark('underline').run()} title="下划线 ⌘U">
            <span className="underline font-bold text-sm font-serif leading-none">U</span>
          </GBtn>
          <GBtn active={activeEditor.isActive('strike')} onClick={() => activeEditor.chain().focus().toggleStrike().run()} title="删除线">
            <Strikethrough size={16} />
          </GBtn>
          <GSep />

          {/* 高亮 */}
          <div className="relative">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setGlobalToolbarSubMenu(globalToolbarSubMenu === 'color' ? 'none' : 'color')}
              className={`global-toolbar-btn w-8 h-8 flex items-center justify-center rounded-md transition-all ${
                activeEditor.isActive('highlight') ? 'bg-amber-100 text-amber-600' : 'text-slate-500 hover:bg-slate-200/60'
              }`}
              title="背景高亮"
            >
              <span className="font-bold text-sm bg-amber-200/80 px-1.5 rounded border border-amber-300/30">A</span>
            </button>
            {globalToolbarSubMenu === 'color' && (
              <div className="global-toolbar-panel absolute left-0 top-full mt-1 p-2 w-40 bg-white border border-slate-200 rounded-xl shadow-lg z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                <div className="grid grid-cols-5 gap-1.5 justify-items-center">
                  {[
                    { color: '#ffe066', label: '柠檬黄' },
                    { color: '#a5d6a7', label: '薄荷绿' },
                    { color: '#90caf9', label: '冰川蓝' },
                    { color: '#f8bbd0', label: '樱花粉' },
                    { color: '#e0e0e0', label: '优雅灰' },
                  ].map((item) => (
                    <button
                      key={item.color}
                      onClick={() => { activeEditor.chain().focus().toggleMark('highlight', { color: item.color }).run(); setGlobalToolbarSubMenu('none'); }}
                      className="rounded-full border border-black/10 transition-all hover:scale-110 flex items-center justify-center"
                      style={{ backgroundColor: item.color, width: '22px', height: '22px' }}
                      title={item.label}
                    >
                      {activeEditor.isActive('highlight', { color: item.color }) && <span className="text-[9px] text-black/60 font-bold">✓</span>}
                    </button>
                  ))}
                </div>
                {activeEditor.isActive('highlight') && (
                  <button
                    onClick={() => { activeEditor.chain().focus().unsetMark('highlight').run(); setGlobalToolbarSubMenu('none'); }}
                    className="mt-2 w-full text-center py-1 bg-slate-100 hover:bg-red-50 hover:text-red-500 rounded-md text-[10px] text-slate-500 font-medium transition-colors"
                  >
                    清除高亮
                  </button>
                )}
              </div>
            )}
          </div>
          <GSep />

          {/* 行内代码 & 链接 */}
          <GBtn active={activeEditor.isActive('code')} onClick={() => activeEditor.chain().focus().toggleCode().run()} title="行内代码">
            <Code size={16} />
          </GBtn>
          <GBtn active={activeEditor.isActive('link')} onClick={() => activeEditor.triggerInsertLink?.()} title="超链接">
            <Link2 size={16} />
          </GBtn>
          <GSep />

          {/* 列表样式 — 无序 + 有序多种样式平铺 */}
          <GBtn active={activeEditor.isActive('bulletList')} onClick={() => activeEditor.chain().focus().toggleBulletList().run()} title="无序列表">
            <List size={16} />
          </GBtn>
          {/* 有序列表各种序号样式直接平铺 */}
          {([
            { label: '1.', title: '数字序号', style: 'decimal' },
            { label: '(1)', title: '括号数字', style: 'decimal-paren' },
            { label: '[1]', title: '方括号数字', style: 'decimal-bracket' },
            { label: '①', title: '带圈数字', style: 'circled-decimal' },
            { label: 'A.', title: '大写字母', style: 'upper-alpha' },
            { label: 'a.', title: '小写字母', style: 'lower-alpha' },
            { label: 'i.', title: '罗马数字', style: 'lower-roman' },
          ] as const).map((item) => {
            const isOrdered = activeEditor.isActive('orderedList');
            const currentStyle = isOrdered ? (activeEditor.getAttributes('orderedList').listStyleType || 'decimal') : null;
            const isThisStyle = isOrdered && currentStyle === item.style;

            return (
              <button
                key={item.style}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (isThisStyle) {
                    // 同一样式再次点击 → 取消有序列表
                    activeEditor.chain().focus().toggleOrderedList().run();
                  } else {
                    // 先确保是有序列表（如果不是就创建）
                    const chain = activeEditor.chain().focus();
                    if (!isOrdered) chain.toggleOrderedList();
                    // 然后用 tr.doc + tr.selection 查找 orderedList（反映前序命令的效果）
                    chain.command(({ tr }: any) => {
                      const $from = tr.doc.resolve(tr.selection.from);
                      for (let depth = $from.depth; depth > 0; depth--) {
                        const node = $from.node(depth);
                        if (node.type.name === 'orderedList') {
                          const pos = $from.before(depth);
                          tr.setNodeMarkup(pos, undefined, { ...node.attrs, listStyleType: item.style });
                          return true;
                        }
                      }
                      return false;
                    }).run();
                  }
                }}
                title={item.title}
                className={`px-1.5 h-8 flex items-center justify-center rounded-md text-xs font-mono font-semibold transition-all whitespace-nowrap ${
                  isThisStyle
                    ? 'bg-sky-100 text-sky-600 shadow-sm'
                    : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-700'
                }`}
              >{item.label}</button>
            );
          })}
          <GSep />


          {/* 快捷组件插入 */}
          <GBtn active={activeEditor.isActive('codeBlock')} onClick={() => activeEditor.chain().focus().toggleCodeBlock().run()} title="代码块">
            <Terminal size={16} />
          </GBtn>
          <GBtn active={activeEditor.isActive('table')} onClick={() => activeEditor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="插入表格">
            <Table size={16} />
          </GBtn>
          <GBtn active={false} onClick={() => activeEditor.triggerImageUpload?.()} title="插入插图">
            <ImageIcon size={16} />
          </GBtn>
          <GBtn active={activeEditor.isActive('blockquote')} onClick={() => activeEditor.chain().focus().toggleBlockquote().run()} title="块级引用">
            <Quote size={16} />
          </GBtn>
          <GBtn active={false} onClick={() => activeEditor.chain().focus().setHorizontalRule().run()} title="分割线">
            <Minus size={16} />
          </GBtn>
        </div>
      )}

      {/* ── 主区域 ── */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* 左侧 Q/A/N 大纲 */}
        <div className="hidden xl:block w-56 shrink-0 border-r border-slate-100 overflow-y-auto py-6 px-3">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-4 pl-3 flex items-center gap-1.5">
            <List size={11} />
            文档结构
          </p>
          <div className="flex flex-col gap-0.5 text-xs">
            {/* Q 问题 */}
            <button
              onMouseDown={(e) => { e.preventDefault(); contentAreaRef.current?.querySelector('#section-q')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              className="w-full text-left px-3 py-2 rounded-lg font-semibold text-sky-600 bg-sky-50/60 flex items-center gap-2"
            >
              <span className="w-5 h-5 rounded-md bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">Q</span>
              <span className="truncate">{question.trim() || '问题'}</span>
            </button>

            {/* A 回答 */}
            <button
              onMouseDown={(e) => { e.preventDefault(); contentAreaRef.current?.querySelector('#section-a')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              className="w-full text-left px-3 py-2 rounded-lg font-semibold text-emerald-600 bg-emerald-50/60 flex items-center gap-2 mt-1"
            >
              <span className="w-5 h-5 rounded-md bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">A</span>
              回答
            </button>

            {/* A 的子标题 */}
            {editorHeadings.length > 0 && (
              <div className="relative border-l border-emerald-200/60 ml-5 flex flex-col gap-0.5">
                {editorHeadings.map((heading) => (
                  <button
                    key={heading.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      scrollToHeading(heading);
                    }}
                    style={{ paddingLeft: `${(heading.level - 1) * 10 + 10}px` }}
                    className={`w-full text-left py-1.5 pr-2 rounded-r-lg transition-all duration-200 border-l-2 -ml-[1.5px] ${
                      activeHeadingId === heading.id
                        ? 'border-emerald-500 text-emerald-600 font-semibold bg-emerald-50/50'
                        : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-100/50'
                    }`}
                  >
                    <span className="line-clamp-1">{heading.text}</span>
                  </button>
                ))}
              </div>
            )}

            {/* N 附件与备注 */}
            <button
              onMouseDown={(e) => { e.preventDefault(); contentAreaRef.current?.querySelector('#section-n')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              className="w-full text-left px-3 py-2 rounded-lg font-semibold text-amber-600 bg-amber-50/60 flex items-center gap-2 mt-1"
            >
              <span className="w-5 h-5 rounded-md bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">N</span>
              附件与备注
            </button>
          </div>
        </div>

        {/* 内容编辑区 */}
        <div ref={contentAreaRef} className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-5 sm:px-8 sm:py-8 space-y-4 sm:space-y-6">

            {/* ═══ Q 问题区块 ═══ */}
            <div id="section-q" className="rounded-2xl border border-sky-200/60 bg-gradient-to-r from-sky-50/50 to-transparent">
              <div className="flex items-center gap-2 px-6 py-3 border-b border-sky-100 bg-sky-50/80 rounded-t-2xl">
                <span className="w-6 h-6 rounded-lg bg-sky-500 text-white text-xs font-bold flex items-center justify-center">Q</span>
                <span className="text-sm font-semibold text-sky-700">问题</span>
              </div>
              <div className="px-6 py-5 space-y-5">
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="输入问题标题..."
                  className="w-full text-xl sm:text-2xl font-bold text-slate-700 bg-transparent border-none outline-none placeholder:text-slate-300 leading-tight"
                  autoFocus
                />

                {/* 标签区 — 各占一行 */}
                <div className="space-y-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider shrink-0 w-14">一级标签</label>
                    {allPrimaryTags.map((t) => {
                      const selected = primaryTagIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setPrimaryTagIds((prev) =>
                            selected ? prev.filter((id) => id !== t.id) : [...prev, t.id]
                          )}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                            selected
                              ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
                              : 'bg-white text-slate-500 border-slate-200 hover:border-sky-300 hover:text-sky-600'
                          }`}
                        >{t.name}</button>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider shrink-0 w-14">二级标签</label>
                    {allSecondaryTags.map((t) => {
                      const selected = secondaryTagIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setSecondaryTagIds((prev) =>
                            selected ? prev.filter((id) => id !== t.id) : [...prev, t.id]
                          )}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                            selected
                              ? 'bg-purple-500 text-white border-purple-500 shadow-sm'
                              : 'bg-white text-slate-500 border-slate-200 hover:border-purple-300 hover:text-purple-600'
                          }`}
                        >{t.name}</button>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider shrink-0 w-14">可见性</label>
                    <button
                      type="button"
                      onClick={() => setVisibilityScope('all')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                        visibilityScope === 'all'
                          ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm'
                          : 'bg-white text-slate-500 border-slate-200 hover:border-emerald-300 hover:text-emerald-600'
                      }`}
                    >
                      外部可见
                    </button>
                    <button
                      type="button"
                      onClick={() => setVisibilityScope('internal')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                        visibilityScope === 'internal'
                          ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                          : 'bg-white text-slate-500 border-slate-200 hover:border-amber-300 hover:text-amber-600'
                      }`}
                    >
                      仅内部可见
                    </button>
                  </div>
                </div>

                {/* 封面图 — 可折叠，默认收起 */}
                <div>
                  <button
                    type="button"
                    onClick={() => setCoverExpanded((v) => !v)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors"
                  >
                    <ChevronRight size={14} className={`transition-transform duration-200 ${coverExpanded ? 'rotate-90' : ''}`} />
                    封面图（可选）
                    {coverImage && <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />}
                  </button>
                  {coverExpanded && (
                    <div className="mt-3">
                      {coverImage ? (
                        <div className="relative group rounded-2xl overflow-hidden border border-slate-200">
                          <img src={coverImage} alt="封面" className="w-full max-h-[280px] object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-3">
                              <label className="flex items-center gap-1.5 px-4 py-2 bg-white rounded-xl text-sm font-medium cursor-pointer hover:bg-slate-100 shadow-sm">
                                <Upload size={14} /> 更换
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCoverUpload(f); }} />
                              </label>
                              <button onClick={() => setCoverImage('')} className="flex items-center gap-1.5 px-4 py-2 bg-white rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 shadow-sm">
                                <X size={14} /> 移除
                              </button>
                            </div>
                          </div>
                          {coverUploading && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                              <Loader2 size={24} className="animate-spin text-sky-500" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <label className={`flex flex-col items-center justify-center py-8 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                            coverUploading ? 'border-sky-300 bg-sky-50' : 'border-slate-200 hover:border-sky-300 hover:bg-sky-50/30'
                          }`}>
                            {coverUploading ? <Loader2 size={24} className="animate-spin text-sky-500 mb-2" /> : <Upload size={24} className="text-slate-400 mb-2" />}
                            <span className="text-sm font-medium text-slate-500">{coverUploading ? '上传中...' : '点击上传文件'}</span>
                            <span className="text-[10px] text-slate-400 mt-1">支持 JPG、PNG、GIF、WebP</span>
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCoverUpload(f); }} disabled={coverUploading} />
                          </label>
                          <div
                            ref={pasteAreaRef} tabIndex={0}
                            onClick={() => { setPasteMode(true); pasteAreaRef.current?.focus(); }}
                            onPaste={handlePaste} onBlur={() => setPasteMode(false)}
                            className={`flex flex-col items-center justify-center py-8 rounded-2xl border-2 border-dashed transition-all outline-none cursor-pointer ${
                              pasteMode ? 'border-sky-400 bg-sky-50 ring-2 ring-sky-200' : 'border-slate-200 hover:border-sky-300 hover:bg-sky-50/30'
                            }`}
                          >
                            <Clipboard size={24} className={`mb-2 ${pasteMode ? 'text-sky-500' : 'text-slate-400'}`} />
                            <span className={`text-sm font-medium ${pasteMode ? 'text-sky-600' : 'text-slate-500'}`}>{pasteMode ? '现在按 Ctrl+V 粘贴' : '点击后粘贴图片'}</span>
                            <span className="text-[10px] text-slate-400 mt-1">{pasteMode ? '等待粘贴中...' : '先点击此区域，再 Ctrl+V'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ═══ A 回答区块 ═══ */}
            <div id="section-a" className="rounded-2xl border border-emerald-200/60 bg-gradient-to-r from-emerald-50/50 to-transparent">
              <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-emerald-100 bg-emerald-50/80 rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">A</span>
                  <span className="text-sm font-semibold text-emerald-700">回答</span>
                </div>
                <div className="flex items-center rounded-lg border border-emerald-200 bg-white p-0.5">
                  <button
                    type="button"
                    onClick={() => handleAnswerModeChange('tiptap')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                      answerMode === 'tiptap'
                        ? 'bg-emerald-500 text-white shadow-sm'
                        : 'text-slate-500 hover:bg-emerald-50 hover:text-emerald-600'
                    }`}
                  >
                    富文本
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAnswerModeChange('html')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                      answerMode === 'html'
                        ? 'bg-emerald-500 text-white shadow-sm'
                        : 'text-slate-500 hover:bg-emerald-50 hover:text-emerald-600'
                    }`}
                  >
                    HTML
                  </button>
                </div>
              </div>
              <div className="px-6 py-5">
                {answerMode === 'html' ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500">
                          {htmlPane === 'code' ? 'HTML 代码' : '实时预览'}
                        </span>
                        <span className="text-[11px] text-slate-400">{answerHtml.length.toLocaleString()} 字符</span>
                      </div>
                      <div className="flex items-center rounded-lg border border-slate-200 bg-white p-0.5">
                        <button
                          type="button"
                          onClick={() => setHtmlPane('code')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            htmlPane === 'code'
                              ? 'bg-slate-900 text-white shadow-sm'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                          }`}
                        >
                          <Code size={13} />
                          代码
                        </button>
                        <button
                          type="button"
                          onClick={() => setHtmlPane('preview')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            htmlPane === 'preview'
                              ? 'bg-emerald-500 text-white shadow-sm'
                              : 'text-slate-500 hover:bg-emerald-50 hover:text-emerald-600'
                          }`}
                          title="预览 HTML"
                        >
                          <Eye size={13} />
                          预览
                        </button>
                      </div>
                    </div>
                    {htmlPane === 'code' ? (
                      <div className="min-w-0">
                        <textarea
                          value={answerHtml}
                          onChange={(e) => setAnswerHtml(e.target.value)}
                          onFocus={() => setActiveEditor(null)}
                          spellCheck={false}
                          placeholder="粘贴完整 HTML..."
                          className="h-[640px] w-full resize-none rounded-xl border border-slate-200 bg-slate-950 px-4 py-3 font-mono text-xs leading-6 text-slate-100 outline-none transition-all placeholder:text-slate-500 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <FaqHtmlSandboxPreview html={answerHtml} minHeight="640px" />
                      </div>
                    )}
                  </div>
                ) : (
                  <FaqRichEditor
                    content={answerJson}
                    onChange={setAnswerJson}
                    placeholder="输入回答内容... 支持插入图片、视频链接、代码块、表格等"
                    minHeight="400px"
                    uploadImage={uploadImage}
                    onEditorReady={handleEditorReady}
                    onFocus={handleEditorFocus}
                    onUploadTask={registerUploadTask}
                  />
                )}
              </div>
            </div>

            {/* ═══ N 附件与备注区块 ═══ */}
            <div id="section-n" className="rounded-2xl border border-amber-200/60 bg-gradient-to-r from-amber-50/50 to-transparent">
              <div className="flex items-center justify-between px-6 py-3 border-b border-amber-100 bg-amber-50/80 rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-lg bg-amber-500 text-white text-xs font-bold flex items-center justify-center">N</span>
                  <span className="text-sm font-semibold text-amber-700">附件与备注</span>
                </div>
                <span className="text-[11px] text-amber-400">截图、参考文档、注意事项等补充资源</span>
              </div>
              <div className="px-6 py-5 space-y-4">
                <FaqRichEditor
                  content={notesJson}
                  onChange={setNotesJson}
                  placeholder="放置截图、参考文档、注意事项等补充资源..."
                  minHeight="200px"
                  uploadImage={uploadImage}
                  onEditorReady={(editor) => {
                    notesEditorRef.current = editor;
                    if (!activeEditor) setActiveEditor(editor);
                  }}
                  onFocus={handleEditorFocus}
                  onUploadTask={registerUploadTask}
                />

                {/* 附件列表 */}
                {attachments.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-amber-500/80 uppercase tracking-wider flex items-center gap-1.5">
                      <Paperclip size={12} />
                      已上传附件 ({attachments.length})
                    </p>
                    <div className="space-y-1">
                      {attachments.map((att, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-white border border-amber-100 rounded-xl group hover:border-amber-200 transition-all">
                          <FileText size={16} className="text-amber-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-600 truncate">{att.name}</p>
                            <p className="text-[10px] text-slate-400">{(att.size / 1024).toFixed(1)} KB · {att.type.split('/').pop()}</p>
                          </div>
                          <button
                            onClick={() => setPreviewAttachment(att)}
                            className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                            title="预览"
                          >
                            <Eye size={14} />
                          </button>
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-slate-400 hover:text-sky-500 hover:bg-sky-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                            title="下载"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download size={14} />
                          </a>
                          <button
                            onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                            title="移除"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 文件上传区 */}
                <label
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                    fileUploading
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50/30'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.dataTransfer.files.length > 0) handleFileUpload(e.dataTransfer.files);
                  }}
                >
                  {fileUploading ? (
                    <Loader2 size={16} className="animate-spin text-amber-500" />
                  ) : (
                    <Paperclip size={16} className="text-slate-400" />
                  )}
                  <span className="text-sm text-slate-500 font-medium">
                    {fileUploading ? '上传中...' : '点击或拖拽添加附件'}
                  </span>
                  <span className="text-[10px] text-slate-400">支持任意文件类型</span>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) handleFileUpload(e.target.files);
                      e.target.value = '';
                    }}
                    disabled={fileUploading}
                  />
                </label>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── 底栏提示 ── */}
      <div className="shrink-0 border-t border-slate-100 px-3 sm:px-6 py-2.5 bg-slate-50/50 flex items-center justify-between text-[11px] text-slate-400">
        <div className="hidden sm:flex items-center gap-3">
          <span>输入 <kbd className="px-1.5 py-0.5 bg-slate-200/60 rounded text-[10px] font-mono">/</kbd> 调出命令菜单</span>
          <span>·</span>
          <span>直接粘贴或拖拽图片到编辑器</span>
        </div>
        <span>
          {question.trim() ? `标题：${question.trim().slice(0, 30)}${question.trim().length > 30 ? '...' : ''}` : '未填写标题'}
        </span>
      </div>

      {/* 附件预览弹窗 */}
      <FaqAttachmentPreview
        attachment={previewAttachment}
        isOpen={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />

      {/* ── 最后 60 秒预警弹窗 ── */}
      {idleRemaining <= 60 && idleRemaining > 0 && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl p-7 max-w-sm mx-4 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <Clock size={26} className="text-amber-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">您已长时间未操作</h3>
            <p className="text-sm text-slate-500 mb-2">
              <span className="text-2xl font-bold text-amber-500">{idleRemaining}</span>
              <span className="ml-1">秒后将自动保存并退出编辑</span>
            </p>
            <p className="text-xs text-slate-400 mb-5">点击任意按钮可继续编辑</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { lastActivityRef.current = Date.now(); onRefreshActivity?.(); }}
                className="px-5 py-2.5 bg-sky-500 text-white text-sm font-semibold rounded-xl hover:bg-sky-600 transition-all shadow-sm"
              >继续编辑</button>
              <button
                onClick={() => handleSave(true)}
                className="px-5 py-2.5 bg-slate-100 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-200 transition-all"
              >保存并退出</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 删除确认弹窗 ── */}
      {deleteConfirm && editingItem && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[360px] max-w-[90vw]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={18} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">确认删除</h3>
            </div>
            <p className="text-sm text-slate-600 mb-1">
              确定要将以下 FAQ 移入回收站吗？
            </p>
            <p className="text-sm font-medium text-slate-800 bg-slate-50 rounded-lg px-3 py-2 mb-3 line-clamp-2">
              「{editingItem.question}」
            </p>
            <p className="text-xs text-slate-400 mb-5">
              移入回收站后，可在管理面板中恢复。
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-all"
              >取消</button>
              <button
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await deleteItem(editingItem.id);
                    setDeleteConfirm(false);
                    onClose();
                  } catch (e) {
                    console.error('Delete failed:', e);
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                className="px-4 py-2 text-sm text-white bg-red-500 rounded-xl hover:bg-red-600 disabled:opacity-50 transition-all flex items-center gap-1.5"
              >
                {deleting && <Loader2 size={14} className="animate-spin" />}
                移入回收站
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
